import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { XCircle, Play } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Card, VStack, HStack, Grid, GridItem } from '@components/base/layout';
import { Breadcrumb } from '@components/base/navigation';
import { Table } from '@components/base/data';
import { Alert, Spinner } from '@components/base/feedback';
import { workflowExecutionsService } from '../../api/workflowExecutionsService';
import type { WorkflowStepExecution } from '../../api/workflowExecutionsService';

// ---------------------------------------------------------------------------
// Status variant mapping
// ---------------------------------------------------------------------------

const statusVariantMap: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'default',
  running: 'info',
  paused: 'warning',
  waiting_input: 'warning',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
};

// ---------------------------------------------------------------------------
// Helper: DetailField
// ---------------------------------------------------------------------------

function DetailField({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <VStack gap={1}>
      <Text size="sm" color="muted" weight="medium">
        {label}
      </Text>
      <div>{children}</div>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Helper: format duration
// ---------------------------------------------------------------------------

function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt) return '\u2014';
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const ms = end - start;

  if (ms < 1000) return `${String(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${String(minutes)}m ${String(seconds)}s`;
}

function formatDurationMs(ms: number | null): string {
  if (ms === null) return '\u2014';
  if (ms < 1000) return `${String(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ---------------------------------------------------------------------------
// Sub-component: StepExecutionsTable
// ---------------------------------------------------------------------------

function StepExecutionsTable({
  stepExecutions,
}: Readonly<{ stepExecutions: WorkflowStepExecution[] }>) {
  const sorted = [...stepExecutions].sort((a, b) => {
    if (!a.started_at && !b.started_at) return 0;
    if (!a.started_at) return 1;
    if (!b.started_at) return -1;
    return new Date(a.started_at).getTime() - new Date(b.started_at).getTime();
  });

  if (sorted.length === 0) {
    return (
      <Card>
        <Card.Header title="Step Executions" />
        <Card.Content>
          <Alert variant="info" title="No step executions">
            No step executions have been recorded for this workflow execution.
          </Alert>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header
        title="Step Executions"
        action={
          <Badge variant="info" size="sm">
            {sorted.length} {sorted.length === 1 ? 'step' : 'steps'}
          </Badge>
        }
      />
      <Card.Content>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Step</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell align="center">Attempt</Table.HeaderCell>
              <Table.HeaderCell>Duration</Table.HeaderCell>
              <Table.HeaderCell>Started</Table.HeaderCell>
              <Table.HeaderCell>Completed</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {sorted.map((stepExec) => (
              <Table.Row key={stepExec.id}>
                <Table.Cell>
                  <VStack gap={1}>
                    <Text weight="medium">
                      {stepExec.step?.name ?? stepExec.workflow_step_id.slice(0, 8)}
                    </Text>
                    {stepExec.error_message && (
                      <Text size="sm" color="muted">
                        {stepExec.error_message}
                      </Text>
                    )}
                  </VStack>
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    variant={statusVariantMap[stepExec.status] ?? 'default'}
                    size="sm"
                  >
                    {stepExec.status}
                  </Badge>
                </Table.Cell>
                <Table.Cell align="center">
                  <Text size="sm">{stepExec.attempt}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm">{formatDurationMs(stepExec.duration_ms)}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm">
                    {stepExec.started_at
                      ? new Date(stepExec.started_at).toLocaleString()
                      : '\u2014'}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm">
                    {stepExec.completed_at
                      ? new Date(stepExec.completed_at).toLocaleString()
                      : '\u2014'}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Card.Content>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function WorkflowExecutionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  // ---- Fetch execution data -------------------------------------------------

  const {
    data: executionResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin', 'workflows', 'executions', id],
    queryFn: () => {
      /* v8 ignore start */
      if (!id) return Promise.reject(new Error('Workflow execution ID is required'));
      /* v8 ignore stop */
      return workflowExecutionsService.get(id);
    },
    enabled: !!id,
  });

  const execution = executionResponse?.data;

  // ---- Mutations ------------------------------------------------------------

  const cancelMutation = useMutation({
    mutationFn: () => {
      /* v8 ignore start */
      if (!id) return Promise.reject(new Error('Workflow execution ID is required'));
      /* v8 ignore stop */
      return workflowExecutionsService.cancel(id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'workflows', 'executions', id],
      });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: () => {
      /* v8 ignore start */
      if (!id) return Promise.reject(new Error('Workflow execution ID is required'));
      /* v8 ignore stop */
      return workflowExecutionsService.resume(id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'workflows', 'executions', id],
      });
    },
  });

  // ---- Render: loading state ------------------------------------------------

  if (isLoading) {
    return (
      <VStack gap={6} align="center" className="py-12">
        <Spinner size="lg" />
        <Text color="muted">Loading workflow execution...</Text>
      </VStack>
    );
  }

  // ---- Render: error state --------------------------------------------------

  if (error || !execution) {
    return (
      <VStack gap={6}>
        <Breadcrumb>
          <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
          <Breadcrumb.Item href="/admin/workflows">Workflows</Breadcrumb.Item>
          <Breadcrumb.Item href="/admin/workflows/executions">Executions</Breadcrumb.Item>
          <Breadcrumb.Item isCurrent>Details</Breadcrumb.Item>
        </Breadcrumb>
        <Alert variant="error" title="Failed to load workflow execution">
          {error instanceof Error
            ? error.message
            : 'Workflow execution not found or an unexpected error occurred.'}
        </Alert>
      </VStack>
    );
  }

  // ---- Derived values -------------------------------------------------------

  const shortId = execution.id.slice(0, 8);
  const showCancel =
    execution.status === 'running' ||
    execution.status === 'paused' ||
    execution.status === 'pending';
  const showResume =
    execution.status === 'paused' || execution.status === 'waiting_input';
  const hasError = execution.error_message || execution.error_code;

  // ---- Render: main ---------------------------------------------------------

  return (
    <VStack gap={6}>
      {/* Breadcrumb navigation */}
      <Breadcrumb>
        <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
        <Breadcrumb.Item href="/admin/workflows">Workflows</Breadcrumb.Item>
        <Breadcrumb.Item href="/admin/workflows/executions">Executions</Breadcrumb.Item>
        <Breadcrumb.Item isCurrent>{shortId}</Breadcrumb.Item>
      </Breadcrumb>

      {/* Page header */}
      <HStack justify="between" align="start">
        <VStack gap={2}>
          <HStack gap={3} align="center">
            <Text as="h1" size="2xl" weight="bold">
              Execution {shortId}
            </Text>
            <Badge
              variant={statusVariantMap[execution.status] ?? 'default'}
              size="sm"
            >
              {execution.status}
            </Badge>
          </HStack>
          {execution.definition?.name && (
            <Text size="sm" color="muted">
              {execution.definition.name}
            </Text>
          )}
        </VStack>

        <HStack gap={2}>
          {showResume && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Icon icon={Play} size="xs" color="inherit" />}
              onClick={() => { resumeMutation.mutate(); }}
              loading={resumeMutation.isPending}
              disabled={cancelMutation.isPending}
            >
              Resume
            </Button>
          )}
          {showCancel && (
            <Button
              variant="danger"
              size="sm"
              leftIcon={<Icon icon={XCircle} size="xs" color="inherit" />}
              onClick={() => { cancelMutation.mutate(); }}
              loading={cancelMutation.isPending}
              disabled={resumeMutation.isPending}
            >
              Cancel
            </Button>
          )}
        </HStack>
      </HStack>

      {/* Execution Details card */}
      <Card>
        <Card.Header title="Execution Details" />
        <Card.Content>
          <Grid columns={2} gap={4}>
            <GridItem>
              <DetailField label="Execution ID">
                <Text size="sm" className="font-mono">
                  {execution.id}
                </Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Workflow Definition">
                <Text size="sm">{execution.definition?.name ?? '\u2014'}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Status">
                <Badge
                  variant={statusVariantMap[execution.status] ?? 'default'}
                  size="sm"
                >
                  {execution.status}
                </Badge>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Current Step">
                <Text size="sm">{execution.current_step?.name ?? '\u2014'}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Started At">
                <Text size="sm">
                  {execution.started_at
                    ? new Date(execution.started_at).toLocaleString()
                    : '\u2014'}
                </Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Completed At">
                <Text size="sm">
                  {execution.completed_at
                    ? new Date(execution.completed_at).toLocaleString()
                    : '\u2014'}
                </Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Duration">
                <Text size="sm">
                  {formatDuration(execution.started_at, execution.completed_at)}
                </Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Expires At">
                <Text size="sm">
                  {execution.expires_at
                    ? new Date(execution.expires_at).toLocaleString()
                    : '\u2014'}
                </Text>
              </DetailField>
            </GridItem>
          </Grid>
        </Card.Content>
      </Card>

      {/* Error Info card (conditional) */}
      {hasError && (
        <Card>
          <Card.Header title="Error Information" />
          <Card.Content>
            <VStack gap={3}>
              {execution.error_code && (
                <DetailField label="Error Code">
                  <Badge variant="error" size="sm">
                    {execution.error_code}
                  </Badge>
                </DetailField>
              )}
              {execution.error_message && (
                <DetailField label="Error Message">
                  <Text>{execution.error_message}</Text>
                </DetailField>
              )}
            </VStack>
          </Card.Content>
        </Card>
      )}

      {/* Context Data card */}
      <Card>
        <Card.Header title="Context Data" />
        <Card.Content>
          {execution.context_data && Object.keys(execution.context_data).length > 0 ? (
            <pre
              className="font-mono text-sm rounded-md bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-4 overflow-auto"
            >
              {JSON.stringify(execution.context_data, null, 2)}
            </pre>
          ) : (
            <Text size="sm" color="muted">
              No context data available
            </Text>
          )}
        </Card.Content>
      </Card>

      {/* Step Executions table */}
      <StepExecutionsTable stepExecutions={execution.step_executions ?? []} />
    </VStack>
  );
}
